// Google Analytics 4 tracking utilities
declare global {
  interface Window {
    gtag: (command: string, targetId: string, config?: any) => void;
    dataLayer: any[];
  }
}

export interface AnalyticsEvent {
  event_name: string;
  event_category?: string;
  event_label?: string;
  value?: number;
  custom_parameter_1?: string;
  custom_parameter_2?: string;
  custom_parameter_3?: string;
}

export interface UserProperties {
  user_id?: string;
  subscription_tier?: 'free' | 'pro' | 'team' | 'enterprise';
  signup_date?: string;
  total_comparisons?: number;
  preferred_models?: string[];
}

class Analytics {
  private isInitialized = false;
  private measurementId: string;
  private isDevelopment: boolean;

  constructor() {
    const DEFAULT_MEASUREMENT_ID = 'G-FZXP036BXC';
    this.measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID;
    this.isDevelopment = import.meta.env.DEV;
  }

  // Initialize Google Analytics
  initialize() {
    if (this.isInitialized || !this.measurementId || this.isDevelopment) {
      console.log('[Analytics] Skipping GA initialization:', {
        isInitialized: this.isInitialized,
        hasMeasurementId: !!this.measurementId,
        isDevelopment: this.isDevelopment
      });
      return;
    }

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, {
      page_title: 'Smart Prompt: One Trusted Answer from Many AIs',
      page_location: window.location.href,
      page_path: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      send_page_view: true,
      anonymize_ip: true,
      allow_google_signals: false,
      cookie_flags: 'SameSite=Strict;Secure'
    });

    this.isInitialized = true;
    console.log('[Analytics] Google Analytics initialized');
  }

  // Track custom events
  trackEvent(event: AnalyticsEvent) {
    if (!this.isInitialized || this.isDevelopment) {
      console.log('[Analytics] Event tracking (dev mode):', event);
      return;
    }

    window.gtag('event', event.event_name, {
      event_category: event.event_category,
      event_label: event.event_label,
      value: event.value,
      custom_parameter_1: event.custom_parameter_1,
      custom_parameter_2: event.custom_parameter_2,
      custom_parameter_3: event.custom_parameter_3
    });
  }

  // Track page views
  trackPageView(page_title: string, page_location: string, page_path?: string) {
    if (!this.isInitialized || this.isDevelopment) {
      console.log('[Analytics] Page view (dev mode):', { page_title, page_location, page_path });
      return;
    }

    window.gtag('config', this.measurementId, {
      page_title,
      page_location,
      page_path,
      send_page_view: true
    });
  }

  // Set user properties
  setUserProperties(properties: UserProperties) {
    if (!this.isInitialized || this.isDevelopment) {
      console.log('[Analytics] User properties (dev mode):', properties);
      return;
    }

    window.gtag('config', this.measurementId, {
      user_id: properties.user_id,
      custom_map: {
        subscription_tier: properties.subscription_tier,
        signup_date: properties.signup_date,
        total_comparisons: properties.total_comparisons,
        preferred_models: properties.preferred_models?.join(',')
      }
    });
  }

  // Business-specific tracking methods
  trackComparison = {
    started: (models: string[], prompt_length: number, user_tier: string) => {
      this.trackEvent({
        event_name: 'comparison_started',
        event_category: 'engagement',
        event_label: `models_${models.length}`,
        value: models.length,
        custom_parameter_1: models.join(','),
        custom_parameter_2: user_tier,
        custom_parameter_3: prompt_length.toString()
      });
    },

    completed: (models: string[], total_time: number, winning_model: string) => {
      this.trackEvent({
        event_name: 'comparison_completed',
        event_category: 'engagement',
        event_label: 'comparison_success',
        value: Math.round(total_time / 1000), // Convert to seconds
        custom_parameter_1: models.join(','),
        custom_parameter_2: winning_model,
        custom_parameter_3: models.length.toString()
      });
    },

    failed: (models: string[], error_type: string, step: string) => {
      this.trackEvent({
        event_name: 'comparison_failed',
        event_category: 'error',
        event_label: error_type,
        custom_parameter_1: models.join(','),
        custom_parameter_2: step,
        custom_parameter_3: models.length.toString()
      });
    }
  };

  trackSubscription = {
    planViewed: (plan: string, current_tier: string) => {
      this.trackEvent({
        event_name: 'subscription_plan_viewed',
        event_category: 'monetization',
        event_label: plan,
        custom_parameter_1: current_tier,
        custom_parameter_2: 'plan_comparison'
      });
    },

    upgradeStarted: (from_tier: string, to_tier: string, price: number) => {
      this.trackEvent({
        event_name: 'subscription_upgrade_started',
        event_category: 'monetization',
        event_label: `${from_tier}_to_${to_tier}`,
        value: price,
        custom_parameter_1: from_tier,
        custom_parameter_2: to_tier
      });
    },

    upgradeCompleted: (from_tier: string, to_tier: string, price: number) => {
      this.trackEvent({
        event_name: 'purchase',
        event_category: 'monetization',
        event_label: 'subscription_upgrade',
        value: price,
        custom_parameter_1: from_tier,
        custom_parameter_2: to_tier
      });
    },

    upgradeFailed: (from_tier: string, to_tier: string, error_type: string) => {
      this.trackEvent({
        event_name: 'subscription_upgrade_failed',
        event_category: 'error',
        event_label: error_type,
        custom_parameter_1: from_tier,
        custom_parameter_2: to_tier
      });
    }
  };

  trackUserEngagement = {
    dashboardVisit: (section: string, user_tier: string) => {
      this.trackEvent({
        event_name: 'dashboard_section_viewed',
        event_category: 'engagement',
        event_label: section,
        custom_parameter_1: user_tier
      });
    },

    featureUsed: (feature: string, context: string, user_tier: string) => {
      this.trackEvent({
        event_name: 'feature_used',
        event_category: 'engagement',
        event_label: feature,
        custom_parameter_1: context,
        custom_parameter_2: user_tier
      });
    },

    helpViewed: (article: string, from_page: string) => {
      this.trackEvent({
        event_name: 'help_article_viewed',
        event_category: 'support',
        event_label: article,
        custom_parameter_1: from_page
      });
    },

    feedbackSubmitted: (rating: number, category: string, has_text: boolean) => {
      this.trackEvent({
        event_name: 'feedback_submitted',
        event_category: 'engagement',
        event_label: category,
        value: rating,
        custom_parameter_1: has_text ? 'with_text' : 'rating_only'
      });
    }
  };

  trackAuthentication = {
    signupStarted: (method: string) => {
      this.trackEvent({
        event_name: 'sign_up_started',
        event_category: 'authentication',
        event_label: method
      });
    },

    signupCompleted: (method: string) => {
      this.trackEvent({
        event_name: 'sign_up',
        event_category: 'authentication',
        event_label: method
      });
    },

    loginCompleted: (method: string, returning_user: boolean) => {
      this.trackEvent({
        event_name: 'login',
        event_category: 'authentication',
        event_label: method,
        custom_parameter_1: returning_user ? 'returning' : 'new'
      });
    }
  };

  trackPerformance = {
    slowLoading: (page: string, load_time: number) => {
      this.trackEvent({
        event_name: 'slow_page_load',
        event_category: 'performance',
        event_label: page,
        value: Math.round(load_time)
      });
    },

    errorOccurred: (error_type: string, page: string, user_action: string) => {
      this.trackEvent({
        event_name: 'error_occurred',
        event_category: 'error',
        event_label: error_type,
        custom_parameter_1: page,
        custom_parameter_2: user_action
      });
    }
  };
}

// Create singleton instance
export const analytics = new Analytics();

// Initialize analytics when the module loads (browser only)
if (typeof window !== 'undefined') {
  analytics.initialize();
}

export default analytics;